import React, { useState, useEffect, useRef, useMemo } from "react";
import { StepperTypeAttributes } from "../../../enums/StepperTypeAttributes";
import TNStepperForm from "../../TNStepperForm";
import { Button, message, Modal } from "antd";
import styles from "./CreateJob.module.scss";
import BackButton from "../../../assets/images/back-button.svg";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import Form from "../../Form";
import JobLead from "./JobLead";
import MaterialTruckDetails from "./MaterialTruckDetails";
import * as Yup from "yup";
import {
  getDefaultValues,
  getEditDefaultValues,
  getValidationSchema,
  jobStatus,
  InternalApprovalSubSections,
  approvalSubSections,
  JobLeadProgressionMessages,
  projectInformationSheetSection,
  hasStatusChanged,
  buildQuotePastStates,
  hasChangesInJobLead,
  hasApprovalInfoChanged,
  quoteApprovedStates,
  internalApprovalPastStates,
  DispatchModalConstants,
  SaveDraftConsts,
  createJobLeadDraftValidation,
  internalApprovalDraftValidation,
  NavButtonConstants,
  getStageId,
  steps,
  cashOrCreditAllowedSections,
  normalizeEmpty,
} from "./constants";
import {
  getFormattedOrderList,
  getFormattedApprovalDocuments,
  validateRejectedSectionsHaveDocs,
  hasDocsForSections,
} from "../../../utils/createJobUtils";
import CreateJobServices from "../../../services/createJob.services";
import BuildQuote from "./BuildQuote";
import { useTruckDetailContext } from "../../../context/TruckDetailsContext";
import TNLoader from "../../../reusable-components/TNLoader";
import CustomerApproval from "./CustomerApproval";
import { useQueryClient } from "@tanstack/react-query";
import { useMaterials } from "../../../context/materialListingContext";
import { useTruckMaterialFormFlowContext } from "../../../context/TruckMaterialFormFlowContext";
import { formatTruckDetails } from "../../../utils/stepTwoPayloadObject";
import { generatePath, useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { useWrapperContext } from "../../../context/WrapperContext";
import InternalApproval from "./InternalApproval";
import greenTickIcon from "../../../assets/images/accept-icon.svg";
import { ProjectInformationSheetState } from "../../../enums/ProjectInformationSheetState";
import { TaxExemptCertificateState } from "../../../enums/TaxExemptCertificateState";
import { appRoutes } from "../../../constants/appRoutes";
import { SectionStatus } from "../../../enums/SubSectionStatus";
import { DocTypes } from "../../../enums/DocTypes";
import {
  BondStatusValue,
  taxExemptStatusValue,
} from "./InternalApproval/constants";
import { getRoutePath } from "../../../utils/routerPathUtils";
import { storageKeys } from "../../../constants/storageKeys";
import {
  TruckMaterialJobType,
  TruckMaterialType,
} from "../../../enums/truckMaterialType";
import moment from "moment";
import { DateFormat } from "../../../enums/dateTimeFormat";
import { ButtonSize, ButtonType } from "../../../enums/ButtonAttributesEnums";
import { ApiResponse } from "../../../enums/ApiResponse";
import RolesServices from "../../../services/roles.services";
import { UserRoleActions } from "../../../enums/UserRolesEnum";

const CreateJobForm = () => {
  const {
    jobCreatedSuccessfully,
    jobUpdatedSuccessfully,
    quoteSubmittedSuccessfully,
    uploadCustomerApprovalFile,
    atleastOneFileIsNeeded,
    projectSheetIsNeeded,
    addTruckDetails,
    quoteInfoSubmissionSuccessfully,
    ordersReadyForDispatch,
    downloadQuoteToProceed,
    failedToSubmitQuote,
    bondInfoDocumentNeeded,
    taxExemptDocumentNeeded,
    projectDocumentsNeeded,
    jobLeadRestriction,
    projectInformationRequired,
    taxExemptRequired,
    saveDraftSuccess,
    failedToSaveDraft,
  } = JobLeadProgressionMessages;
  const { SAVE, SAVE_AND_CONTINUE, SAVE_DRAFT, PREVIOUS, CONTINUE } =
    NavButtonConstants;
  const location = useLocation();
  const navigate = useHistory();
  const isSubmittingRef = useRef(false);
  const isContinueSubmitRef = useRef(false);
  const isDraftSavingRef = useRef<boolean>(false);
  const {
    jobData,
    edit,
    jobSpecificsData,
    step,
    source,
    navigatedFromCxDb,
    selectedCustomer,
  } = location.state || {};
  const [prevEditValue, setPrevEditValue] = useState({});
  const editable = location.pathname?.includes(
    "/app/update-trucking-material/",
  );
  const { ADD_JOB } = appRoutes;
  const queryClient = useQueryClient();
  const { prevRecord } = useWrapperContext();
  const { truckDetails, appendTruckDetail, resetTruckDetails } =
    useTruckDetailContext();
  const { truckFormType } = useTruckMaterialFormFlowContext();
  const { materialTruckList, addTruckMaterial, resetTruckMaterial } =
    useMaterials();
  const [currentStep, setCurrentStep] = useState(step ?? 1);
  const [stepOneJobId, setStepOneJobId] = useState("");
  const [stepOneJobNumber, setStepOneJobNumber] = useState<number | string>("");
  const [stepTwoPayload, setStepTwoPayload] = useState([]);
  const userDetails = localStorage.getItem(storageKeys.USER_DETAILS_KEY);
  const userDetailsJson = JSON.parse(userDetails);
  const userRoleInfo = userDetailsJson?.[storageKeys.USER_ROLE_KEY];
  const userRoleProfileInfo = userDetailsJson?.[storageKeys.USER_PROFILE_KEY];

  const { FetchUserRoleDetails } = RolesServices();
  const { data: roleDetails } = FetchUserRoleDetails(userRoleInfo);

  const hasCustomerAccess = roleDetails
    ? roleDetails?.[0]?.otherData?.[2]?.multiPannel?.[0]?.selectedOptions?.includes(
        UserRoleActions.ALLOW,
      )
    : true;

  const {
    FetchCustomerAccounts,
    FetchContacts,
    SaveJobLead,
    SaveJobLeadDraft,
    AddDocuments,
    SaveTruckAndMaterialsDetails,
    FetchSettingsBidConfig,
    FetchSelectedJob,
    SaveInternalApproval,
    FetchAllDocuments,
    ApproveDispatch,
    ApproveInternalComplete,
    DeleteDocuments,
  } = CreateJobServices();
  const { mutateAsync: deleteDocument, isPending: isDeletingDocument } =
    DeleteDocuments();

  const handleDeleteDocument = async (documentIds?: string[]) => {
    const payload = {
      recordIds: [...documentIds],
      leadId: stepOneJobId,
    };
    const data = await deleteDocument(payload);
  };
  const { data: documentsData, refetch: refecthDocuments } =
    FetchAllDocuments(stepOneJobId);
  const {
    data: jobDetails,
    refetch: refetchJobDetails,
    isFetching: isJobsFetching,
    isRefetching: isJobsRefetching,
  } = FetchSelectedJob(stepOneJobId);
  const { data: companiesData } = FetchCustomerAccounts();
  const [companyId, setCompanyId] = useState(undefined);

  const { data: contactsData } = FetchContacts(companyId);
  const validation = getValidationSchema(currentStep, truckFormType);
  const { mutateAsync: saveJobLead, isPending: isLoading } = SaveJobLead();
  const { mutateAsync: saveJobLeadDraft, isPending: savingJobLeadDraft } =
    SaveJobLeadDraft();
  const {
    mutateAsync: saveMaterailAndTruckDetails,
    isPending: isStepTwoLoading,
  } = SaveTruckAndMaterialsDetails();
  const {
    mutateAsync: saveInternalApproval,
    isPending: isSaveInternalApprovalPending,
  } = SaveInternalApproval();
  const { mutateAsync: addDocuments } = AddDocuments();
  const { mutateAsync: approveDispatch } = ApproveDispatch();
  const { mutateAsync: approveInternalComplete } = ApproveInternalComplete();
  const [presentStageId, setPresentStageId] = useState<undefined | string>(
    undefined,
  );
  const [listOfUploadedFiles, setListOfUploadedFiles] = useState([null, null]);
  const [projectFile, setProjectFile] = useState(null);
  const handleProjectUpload = (file) => setProjectFile(file);

  const [taxFile, setTaxFile] = useState(null);
  const handleTaxUpload = (file) => setTaxFile(file);

  const [disableSave, setSaveDisabled] = useState(false);
  const handleCurrentStep = (step) => setCurrentStep(step);
  const [projectInformation, setProjectInformation] = useState("");
  const [taxExempt, setTaxExempt] = useState("");

  const [uploadedCustomerApprovedFiles, setCustomerApprovedFiles] = useState(
    [],
  );
  const [isContinueDisabled, setContinueDisabled] = useState(true);

  const handleCustomerApprovedFiles = (files) =>
    setCustomerApprovedFiles(files);

  const [quoteSharedStatus, setQuoteSharedStatus] = useState(false);

  const handlePrevStep = () => {
    if (currentStep === 2 && jobDetails?.jobDispatched === true) {
      message.warning(jobLeadRestriction);
      return;
    }
    currentStep > 1 ? setCurrentStep(currentStep - 1) : setCurrentStep(1);
  };

  const fileUploadHandler = ({
    projectInformation,
    taxExempt,
  }: {
    projectInformation?: string;
    taxExempt?: string;
  }) => {
    setProjectInformation(projectInformation);
    setTaxExempt(taxExempt);
  };

  const [listOfInternalApprovalFiles, setListOfInternalApprovalFiles] =
    useState([]);
  const [listOfCashOrCreditFiles, setListOfCashOrCreditFiles] = useState([]);
  const handleInternalApprovalFiles = (fileList) =>
    setListOfInternalApprovalFiles(fileList);
  const handleCashOrCreditFiles = (fileList) =>
    setListOfCashOrCreditFiles(fileList);

  const [isProjectApprovedModalOpen, setIsProjectApprovedModal] =
    useState(false);
  const [resetOrders, setResetOrders] = useState();
  const [selectedImportJobId, setSelectedImportJobId] = useState();

  const handleSelectedImportJobId = (jobId) => {
    setSelectedImportJobId(jobId);
  };

  const handleEnableResetOrders = () => {
    setResetOrders(true);
  };

  const handleDisableResetOrders = () => {
    setResetOrders(false);
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      window.location.href = location.state?.prevLocation;
    };
    // const handlePopState = () => {
    //   const confirmed = window.confirm(
    //     "You have unsaved changes. Are you sure you want to leave this page?"
    //   );
    //   if (confirmed) {
    //     // Push the current state back to the history to prevent navigation
    //     window.location.href = "/app/dashboard";
    //   }
    // };
    // Listen for beforeunload and popstate events
    window.addEventListener("beforeunload", handleBeforeUnload);
    // window.addEventListener("popstate", handlePopState);
    // Initial push to the history stack to track the current state
    window.history.pushState(null, "", window.location.href);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const currentStage = (currentStep) => {
    switch (currentStep) {
      case 1:
        return (
          <JobLead
            uploadedFiles={listOfUploadedFiles}
            fileUploadHandler={fileUploadHandler}
            handleProjectFileUpload={handleProjectUpload}
            handleTaxFileUpload={handleTaxUpload}
            jobDetails={jobDetails}
            leadId={stepOneJobId}
            handleEnableResetOrders={handleEnableResetOrders}
            selectedImportJobId={selectedImportJobId}
            handleSelectedImportJobId={handleSelectedImportJobId}
          />
        );
      case 2:
        return (
          <MaterialTruckDetails
            leadId={stepOneJobId}
            isEditable={editable}
            dropOffLatLng={jobDetails?.dropOffLatLng}
            dropOff={jobDetails?.dropoff}
          />
        );
      case 3:
        return (
          <BuildQuote
            leadId={stepOneJobId}
            jobNumber={stepOneJobNumber}
            handleQuoteSharedStatus={setQuoteSharedStatus}
            isEditable={editable}
            jobType={jobDetails?.jobType}
          />
        );
      case 4:
        return (
          <CustomerApproval
            handleCustomerApprovedFiles={handleCustomerApprovedFiles}
            leadId={stepOneJobId}
            editable={editable}
          />
        );
      case 5:
        return (
          <InternalApproval
            jobLeadId={stepOneJobId}
            handleInternalApprovalFiles={handleInternalApprovalFiles}
            handleCashOrCreditFiles={handleCashOrCreditFiles}
            editable={editable}
            taxExempt={taxExempt}
            setTaxExempt={setTaxExempt}
            setContinueDisabled={setContinueDisabled}
          />
        );

      default:
        return (
          <JobLead
            uploadedFiles={listOfUploadedFiles}
            fileUploadHandler={fileUploadHandler}
            handleProjectFileUpload={handleProjectUpload}
            handleTaxFileUpload={handleTaxUpload}
          />
        );
    }
  };

  const currentFormStep = currentStage(currentStep);
  const { data: bidConfigSettingsData } = FetchSettingsBidConfig(undefined);
  const addDefaultValue = getDefaultValues(
    source === appRoutes.CUSTOMER_DATABASE,
    selectedCustomer,
    bidConfigSettingsData,
  );
  const editDefaultValue = getEditDefaultValues(
    prevRecord,
    bidConfigSettingsData,
  );
  const defaultValues = editable ? editDefaultValue : addDefaultValue;
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const handleOpenWarningModal = () => setIsWarningModalOpen(true);
  const handleCloseWarningModal = () => setIsWarningModalOpen(false);
  const handleNavigate = () => {
    if (!!location?.state?.from) {
      if (location?.state?.from?.pathname !== appRoutes.ADD_JOB)
        sessionStorage.setItem(
          storageKeys.ROUTE_DATA_KEY,
          JSON?.stringify({
            ...location?.state?.from?.state,
            source: location?.state?.source,
          }),
        );
      window.location.href = getRoutePath(location?.state?.from);
    } else window.location.href = `${appRoutes.ADD_JOB}#list`;
  };
  const handleSubmitNavigate = () => {
    message.success(quoteInfoSubmissionSuccessfully);
    navigate.push(`/app/trucking-material${window.location?.hash}`);
  };
  const createJobData = queryClient.getQueryData(["createJobDataKey"]);

  const checkIsFilesUploaded = () => {
    const errors: string[] = [];

    if (
      projectInformation === ProjectInformationSheetState.RECEIVED &&
      (!projectFile ||
        (Array.isArray(projectFile) && projectFile?.length === 0))
    ) {
      errors.push(projectInformationRequired);
    }

    if (
      taxExempt === TaxExemptCertificateState.EXEMPT &&
      (!taxFile || (Array.isArray(taxFile) && taxFile?.length === 0))
    ) {
      errors.push(taxExemptRequired);
    }

    return errors;
  };
  useEffect(() => {
    setListOfUploadedFiles([
      projectInformation === ProjectInformationSheetState.NOT_RECEIVED
        ? null
        : listOfUploadedFiles[0],
      taxExempt === TaxExemptCertificateState.NOT_EXEMPT
        ? null
        : listOfUploadedFiles[1],
    ]);
  }, [projectInformation, taxExempt]);
  useEffect(() => {
    if (truckDetails?.length > 0 || materialTruckList?.length > 0) {
      const formattedTruckDetailsPayload = truckDetails?.map(
        (truck) => formatTruckDetails(truck) ?? [],
      );
      const combinedTrucks = [
        ...(materialTruckList ?? []),
        ...(formattedTruckDetailsPayload ?? []),
      ];
      setStepTwoPayload(combinedTrucks);
    }
  }, [truckDetails, materialTruckList]);

  useEffect(() => {}, [uploadedCustomerApprovedFiles, quoteSharedStatus]);

  //Adding truck context
  const truckDetailsList =
    getFormattedOrderList(prevRecord?.material, TruckMaterialType.TRUCK_TYPE) ??
    [];

  const materialDetailsList =
    getFormattedOrderList(
      prevRecord?.material,
      TruckMaterialType.MATERIAL_TYPE,
    ) ?? [];

  const jobIsDispatchApproved = useMemo(() => {
    return [...truckDetailsList, ...materialDetailsList]?.some(
      (item) => item?.locked === true,
    );
  }, [truckDetailsList, materialDetailsList]);

  useEffect(() => {
    if (editable) {
      appendTruckDetail(truckDetailsList);
      materialDetailsList?.forEach((item) => {
        addTruckMaterial(item);
      });
      setStepOneJobId(prevRecord?.id);
      setStepOneJobNumber(prevRecord?.jobNumber ?? "");
    }
  }, []);

  useEffect(() => {
    resetTruckDetails();
    resetTruckMaterial();
    const truckDetailsList =
      getFormattedOrderList(
        jobDetails?.material,
        TruckMaterialType.TRUCK_TYPE,
      ) ?? [];

    const materialDetailsList =
      getFormattedOrderList(
        jobDetails?.material,
        TruckMaterialType.MATERIAL_TYPE,
      ) ?? [];
    appendTruckDetail(truckDetailsList);
    materialDetailsList?.forEach((item) => {
      addTruckMaterial(item);
    });
  }, [jobDetails]);

  const approveDispatchRequest = async () => {
    await approveDispatch(stepOneJobId);
    await refetchJobDetails();
  };
  const approveInternalCompleteRequest = async () => {
    await approveInternalComplete(stepOneJobId);
  };
  useEffect(() => {
    if (
      jobDetails?.stageId === jobStatus.l2Approved ||
      jobDetails?.stageId === jobStatus.internalApprovalComplete
    ) {
      setContinueDisabled(false);
    } else {
      setContinueDisabled(true);
    }
    if (!!jobDetails?.stageId) setPresentStageId(jobDetails?.stageId);
    if (jobDetails?.jobNumber && !stepOneJobNumber) {
      setStepOneJobNumber(Number(jobDetails.jobNumber));
    }
  }, [jobDetails]);

  useEffect(() => {
    if (window.location.pathname?.includes("app/add-trucking-material")) {
      setListOfUploadedFiles([null, null]);
      setPresentStageId(undefined);
    }
  }, []);
  return (
    <div>
      <TNLoader
        isLoading={
          isLoading ||
          isStepTwoLoading ||
          isJobsFetching ||
          isJobsRefetching ||
          isSubmittingRef.current ||
          isContinueSubmitRef.current ||
          isDraftSavingRef.current ||
          savingJobLeadDraft
        }
      />
      <Modal
        visible={isWarningModalOpen}
        onCancel={handleCloseWarningModal}
        title="Confirm Cancel"
        okText="Confirm"
        onOk={() => {
          handleCloseWarningModal();
          handleNavigate();
        }}
      >
        <p style={{ fontSize: "18px" }}>
          If you cancel the edit, all modifications you have made will be lost.
        </p>
      </Modal>
      <Form defaultValues={defaultValues} validationSchema={validation}>
        {({
          watch,
          control: { handleSubmit },
          clearErrors,
          setError,
          setValue,
        }) => {
          const formValues = watch();
          setCompanyId(watch("customerAccount"));
          const saveDraftValidations = async () => {
            const yupObject =
              currentStep === 1
                ? createJobLeadDraftValidation
                : internalApprovalDraftValidation;
            try {
              await yupObject.validate(formValues, { abortEarly: false });
              clearErrors();
              return true;
            } catch (err) {
              if (err instanceof Yup.ValidationError) {
                clearErrors();
                err.inner.forEach((error) => {
                  if (error.path) {
                    setError(error.path, {
                      type: "manual",
                      message: error.message,
                    });
                  }
                });
              }
              return false;
            }
          };
          const handleSaveDraft = async () => {
            if (currentStep === 1) {
              const isValid = await saveDraftValidations();
              if (!isValid) return;
              let contactDetails, selectedCompanyCustomer, selectedContact;
              if (formValues?.customerAccount) {
                selectedCompanyCustomer = companiesData?.find(
                  (item) => item?.id === formValues?.customerAccount,
                );
              }
              if (formValues?.customerAccount && formValues?.contactName) {
                selectedContact = contactsData?.find(
                  (item) => item?.id === formValues?.contactName,
                );

                contactDetails = {
                  companyId: selectedContact?.companyId,
                  company: selectedContact?.company,
                  lastName: selectedContact?.lastName,
                  notes: selectedContact?.notes,
                  isPrimaryContact: selectedContact?.isPrimary ?? false,
                  jobId: selectedContact?.jobId,
                  email: selectedContact?.email,
                  id: selectedContact?.id,
                  phone: selectedContact?.phone,
                  firstName: selectedContact?.firstName,
                  isDriver: selectedContact?.isDriver,
                };
              }
              if (isDraftSavingRef.current || savingJobLeadDraft) return;
              isDraftSavingRef.current = true;
              try {
                const errors = checkIsFilesUploaded();
                if (errors.length > 0) {
                  errors.map((err) => message.error(err));
                  setCurrentStep(1);
                } else {
                  let data = await saveJobLeadDraft(
                    {
                      companyId: formValues?.customerAccount,
                      companyName: selectedCompanyCustomer?.companyName ?? "",
                      jobName: formValues?.jobName,
                      dropOff: formValues?.deliveryAddress,
                      dropOffLatLng: formValues?.deliveryAddressCoordinates,
                      projectInformationSheet: projectInformation,
                      taxExemptStatus: taxExempt,
                      salesPerson: formValues?.salesPerson,
                      contactDetails: !!contactDetails
                        ? contactDetails
                        : undefined,
                      jobId: editable || !!stepOneJobId ? stepOneJobId : "",
                      notes: formValues?.notes,
                      cloneFromJobId: selectedImportJobId,
                      resetOrders: resetOrders,
                    },
                    {
                      onSuccess: (data) => {
                        if (!editable && stepOneJobId === "") {
                          setStepOneJobId(String(data?.leadObj?.id));
                          setStepOneJobNumber(Number(data?.leadObj?.jobNumber));
                          message.success(jobCreatedSuccessfully);
                        }
                        handleDisableResetOrders();
                      },
                      onError: (error) => {
                        if (error.response?.status === 400)
                          message.error(error.response?.data?.message);
                        else message.error(failedToSaveDraft);
                      },
                    },
                  );
                  const documentsData = await refecthDocuments();
                  const documentIds = new Set(
                    documentsData?.data?.map((doc) => doc?.id),
                  );
                  const jobFiles = [];
                  if (projectFile && !Array.isArray(projectFile)) {
                    const isDeprecated =
                      projectFile?.docType ===
                      DocTypes.DEPRECATED_PROJECT_INFORMATION_SHEET;
                    jobFiles.push({
                      ...projectFile,
                      id: isDeprecated ? undefined : projectFile?.id,
                    });
                  }
                  if (taxFile && !Array.isArray(taxFile)) {
                    const isDeprecated =
                      taxFile?.docType ===
                      DocTypes.DEPRECATED_TAX_EXEMPT_CERTIFICATE;
                    jobFiles.push({
                      ...taxFile,
                      id: isDeprecated ? undefined : taxFile?.id,
                    });
                  }
                  const jobFilesIds = new Set(
                    jobFiles.map((file) => file?.id).filter(Boolean),
                  );
                  const newDocuments = jobFiles?.filter(
                    (file) => !documentIds?.has(file?.id),
                  );
                  const deletedDocuments = documentsData?.data?.filter(
                    (doc) =>
                      (doc?.docType === DocTypes.PROJECT_INFORMATION_SHEET ||
                        doc?.docType === DocTypes.TAX_EXEMPT_CERTIFICATE) &&
                      !jobFilesIds?.has(doc?.id),
                  );
                  const deletedDocumentIds = deletedDocuments?.map(
                    (item) => item?.id,
                  );
                  if (deletedDocuments?.length > 0 && !!stepOneJobId) {
                    await deleteDocument({
                      recordIds: [...deletedDocumentIds],
                      leadId: stepOneJobId,
                    });
                  }
                  if (newDocuments.length > 0) {
                    const uploadedFiles = await Promise.all(
                      newDocuments.map(async (file) => {
                        if (file !== null && file !== undefined) {
                          const payload = {
                            ...file,
                            leadId: data?.["leadObj"]?.["id"] ?? stepOneJobId,
                            assignedTo:
                              data?.["leadObj"]?.["jobNumber"] ??
                              stepOneJobNumber,
                            date: moment().format(
                              DateFormat.MM_DD_YYYY_SLASHED,
                            ),
                          };
                          const response = await addDocuments(payload);
                          return response?.message === ApiResponse.SUCCESS
                            ? { ...response.data, name: file.name }
                            : null;
                        }
                      }),
                    );
                  }
                  message.success(saveDraftSuccess);
                  await refetchJobDetails();
                }
              } catch (error) {
                console.error(error);
                isDraftSavingRef.current = false;
              } finally {
                setValue("importJobId", selectedImportJobId);
                isDraftSavingRef.current = false;
              }
            } else if (currentStep === 2) {
              if (isDraftSavingRef.current || savingJobLeadDraft) return;
              isDraftSavingRef.current = true;
              if (
                truckDetails?.length === 0 &&
                materialTruckList?.length === 0
              ) {
                setCurrentStep(2);
                isDraftSavingRef.current = false;
                return message.error(addTruckDetails);
              } else if (
                truckDetails?.length > 0 ||
                materialTruckList?.length > 0
              ) {
                const { data } = await saveMaterailAndTruckDetails(
                  {
                    jobId: stepOneJobId,
                    truckAndMaterials: stepTwoPayload,
                    stageId: jobStatus.materialTruckDetails,
                    isDraft: true,
                  },
                  {
                    onError: (err) => {
                      console.error("Err", err);
                      message.error(failedToSaveDraft);
                    },
                  },
                );

                const freshTrucks =
                  getFormattedOrderList(
                    data?.material,
                    TruckMaterialType.TRUCK_TYPE,
                  ) ?? [];

                const freshMaterials =
                  getFormattedOrderList(
                    data?.material,
                    TruckMaterialType.MATERIAL_TYPE,
                  ) ?? [];

                resetTruckDetails();
                resetTruckMaterial();
                appendTruckDetail(freshTrucks);
                freshMaterials.forEach((item) => addTruckMaterial(item));
                isDraftSavingRef.current = false;
                message.success(saveDraftSuccess);
                refetchJobDetails();
              }
            } else if (currentStep === 5) {
              const isValid = await saveDraftValidations();
              if (!isValid) return;
              if (isDraftSavingRef.current || isLoading) {
                return;
              }
              isDraftSavingRef.current = true;
              const resolvedJobNumber =
                stepOneJobNumber || jobDetails?.jobNumber || "";
              //Tax and Bond Info check is separate
              const internalApprovalActualDocs =
                listOfInternalApprovalFiles?.map((doc) =>
                  doc?.docType === DocTypes.DEPRECATED_BOND_INFO_DOCUMENT
                    ? { ...doc, id: undefined }
                    : doc?.docType ===
                        DocTypes.DEPRECATED_TAX_EXEMPT_CERTIFICATE
                      ? { ...doc, id: undefined }
                      : doc,
                ) ?? [];
              const bondInfoDoc = internalApprovalActualDocs.find(
                (doc) => doc?.docType === DocTypes.BOND_INFO_DOCUMENT,
              );
              if (
                formValues?.projectBondStatus === BondStatusValue.bonded &&
                !bondInfoDoc
              ) {
                isDraftSavingRef.current = false;
                message.error(bondInfoDocumentNeeded);
                return;
              }
              const taxDoc = internalApprovalActualDocs.find(
                (doc) => doc?.docType === DocTypes.TAX_EXEMPT_CERTIFICATE,
              );
              if (taxExempt === taxExemptStatusValue.exempt && !taxDoc) {
                isDraftSavingRef.current = false;
                message.error(taxExemptDocumentNeeded);
                return;
              }
              const freshDocsResult = await refecthDocuments();
              const freshDocumentIds = new Set(
                freshDocsResult?.data?.map((doc) => doc?.id),
              );
              const isCashOrCredit = !!formValues?.cashOrCreditSale;
              let newDocuments, deletedDocuments;

              if (!isCashOrCredit) {
                //NON CASH OR CREDIT REGULAR FLOW
                const approvalFileIds = new Set(
                  internalApprovalActualDocs?.map((doc) => doc?.id),
                );

                newDocuments = internalApprovalActualDocs?.filter(
                  (file) => !freshDocumentIds?.has(file?.id),
                );

                deletedDocuments = freshDocsResult?.data?.filter(
                  (doc) =>
                    (approvalSubSections.includes(doc?.docType) &&
                      !approvalFileIds.has(doc?.id)) ||
                    doc?.docType === DocTypes.CASH_CREDIT_SALE_DOCUMENTS,
                );
              } else {
                // CASH OR CREDIT UPLOAD FLOW
                const bondAndTaxIds = new Set(
                  internalApprovalActualDocs
                    ?.filter(
                      (file) =>
                        file?.docType === DocTypes.BOND_INFO_DOCUMENT ||
                        file?.docType === DocTypes.TAX_EXEMPT_CERTIFICATE,
                    )
                    ?.map((file) => file?.id),
                );
                const cashOrCreditIds = new Set(
                  listOfCashOrCreditFiles?.map((doc) => doc?.id),
                );

                newDocuments = [
                  ...internalApprovalActualDocs?.filter(
                    (file) =>
                      (file?.docType === DocTypes.BOND_INFO_DOCUMENT ||
                        file?.docType === DocTypes.TAX_EXEMPT_CERTIFICATE ||
                        file?.docType ===
                          DocTypes.DEPRECATED_BOND_INFO_DOCUMENT ||
                        file?.docType ===
                          DocTypes.DEPRECATED_TAX_EXEMPT_CERTIFICATE) &&
                      !freshDocumentIds.has(file?.id),
                  ),
                  ...listOfCashOrCreditFiles?.filter(
                    (file) => !freshDocumentIds.has(file?.id),
                  ),
                ];
                deletedDocuments = freshDocsResult?.data?.filter(
                  (doc) =>
                    (approvalSubSections.includes(doc?.docType) &&
                      !bondAndTaxIds.has(doc?.id)) ||
                    (doc?.docType === DocTypes.CASH_CREDIT_SALE_DOCUMENTS &&
                      !cashOrCreditIds.has(doc?.id)),
                );
              }

              const deletedDocumentIds = deletedDocuments?.map(
                (doc) => doc?.id,
              );
              deletedDocumentIds?.length &&
                (await handleDeleteDocument(deletedDocumentIds));
              const approvedSections = isCashOrCredit
                ? cashOrCreditAllowedSections
                : approvalSubSections;
              const isNewDocumentsAdded =
                newDocuments?.some((doc) =>
                  approvedSections.includes(doc?.docType),
                ) || deletedDocumentIds?.length > 0;

              await Promise.all(
                newDocuments?.map(async (file) => {
                  if (file !== null) {
                    const payload = {
                      ...file,
                      leadId: stepOneJobId,
                      assignedTo: resolvedJobNumber,
                      date: moment().format(DateFormat.MM_DD_YYYY_SLASHED),
                      name: file.name,
                    };
                    const response = await addDocuments(payload);
                    return response?.message === ApiResponse.SUCCESS
                      ? { ...response.data, name: file.name }
                      : null;
                  }
                }),
              );
              // contains the newly uploaded IDs as well.
              const documentsDataAfterUpload = await refecthDocuments();
              const cashOrCreditDocuments =
                documentsDataAfterUpload?.data
                  ?.filter(
                    (doc) =>
                      doc?.docType === DocTypes.CASH_CREDIT_SALE_DOCUMENTS,
                  )
                  ?.map((doc) => doc.id)
                  ?.filter(Boolean) ?? [];
              const projectReceivedStatus =
                documentsDataAfterUpload?.data?.some(
                  (doc) => doc?.docType === DocTypes.PROJECT_INFORMATION_SHEET,
                )
                  ? ProjectInformationSheetState.RECEIVED
                  : ProjectInformationSheetState.NOT_RECEIVED;
              const stageId = jobStatus.internalApprovalDraft;
              let approvalInfo = {
                jobId: stepOneJobId,
                name: formValues?.onSiteContactName,
                phone: formValues?.onSiteContactPhone,
                email: formValues?.onSiteContactEmail,
                dropDown1: formValues?.dropdown1,
                dropDown2: formValues?.dropdown2,
                dropDown3: formValues?.dropdown3,
                projectBondStatus: formValues?.projectBondStatus,
                taxExempt: taxExempt,
                jobSite: formValues?.jobSiteAddress,
                jobSiteLat:
                  formValues?.jobSiteAddressCoordinates?.split(",")[0],
                jobSiteLang:
                  formValues?.jobSiteAddressCoordinates?.split(",")[1],
                stageId: stageId,
                projectInformationSheet: projectReceivedStatus,
                poInfo1: formValues?.poInfo1,
                poInfo2: formValues?.poInfo2,
                poInfo3: formValues?.poInfo3,
                poInfo4: formValues?.poInfo4,
                cashOrCreditSale: formValues?.cashOrCreditSale,
              };
              let response;
              const approvalDocuments = getFormattedApprovalDocuments(
                documentsDataAfterUpload?.data,
                jobDetails?.approvalDocuments,
                newDocuments,
                deletedDocuments,
                jobDetails?.stageId === jobStatus?.internalApprovalDraft ||
                  isNewDocumentsAdded,
              );
              const payload = approvalInfo;
              response = await saveInternalApproval({
                ...payload,
                ...(formValues?.cashOrCreditSale
                  ? {
                      cashOrCreditProjectDocs: cashOrCreditDocuments,
                      approvalDocuments: undefined,
                    }
                  : {
                      approvalDocuments: approvalDocuments,
                      cashOrCreditProjectDocs: undefined,
                    }),
              });
              if (response?.message === ApiResponse.SUCCESS) {
                isDraftSavingRef.current = false;
                message.success(saveDraftSuccess);
                const freshDocs = documentsDataAfterUpload?.data ?? [];

                handleCashOrCreditFiles(
                  freshDocs.filter(
                    (doc) =>
                      doc?.docType === DocTypes.CASH_CREDIT_SALE_DOCUMENTS,
                  ),
                );
                handleInternalApprovalFiles(
                  freshDocs.filter(
                    (doc) =>
                      approvalSubSections.includes(doc?.docType) ||
                      doc?.docType === DocTypes.BOND_INFO_DOCUMENT ||
                      doc?.docType === DocTypes.TAX_EXEMPT_CERTIFICATE,
                  ),
                );

                refetchJobDetails();
              } else {
                isDraftSavingRef.current = false;
                message.error(failedToSaveDraft);
              }
            }
            isDraftSavingRef.current = false;
          };

          const handleContinue = async () => {
            if (isContinueSubmitRef.current) return;
            isContinueSubmitRef.current = true;
            // so that files uploaded during a prior draft save are not counted as new.
            const internalApprovalActualDocs =
              listOfInternalApprovalFiles?.map((doc) =>
                doc?.docType === DocTypes.DEPRECATED_BOND_INFO_DOCUMENT
                  ? { ...doc, id: undefined }
                  : doc?.docType === DocTypes.DEPRECATED_TAX_EXEMPT_CERTIFICATE
                    ? { ...doc, id: undefined }
                    : doc,
              ) ?? [];
            const freshDocsForContinue = await refecthDocuments();
            const freshDocumentIdsForContinue = new Set(
              freshDocsForContinue?.data?.map((doc) => doc?.id),
            );

            let newDocuments, deletedDocuments;

            if (formValues?.cashOrCreditSale) {
              // CASH/CREDIT FLOW
              const bondAndTaxIds = new Set(
                internalApprovalActualDocs
                  ?.filter(
                    (file) =>
                      file?.docType === DocTypes.BOND_INFO_DOCUMENT ||
                      file?.docType === DocTypes.TAX_EXEMPT_CERTIFICATE,
                  )
                  ?.map((file) => file?.id),
              );
              const cashOrCreditIds = new Set(
                listOfCashOrCreditFiles?.map((doc) => doc?.id),
              );

              newDocuments = [
                ...(internalApprovalActualDocs?.filter(
                  (file) =>
                    (file?.docType === DocTypes.BOND_INFO_DOCUMENT ||
                      file?.docType === DocTypes.TAX_EXEMPT_CERTIFICATE ||
                      file?.docType ===
                        DocTypes.DEPRECATED_BOND_INFO_DOCUMENT ||
                      file?.docType ===
                        DocTypes.DEPRECATED_TAX_EXEMPT_CERTIFICATE) &&
                    !freshDocumentIdsForContinue.has(file?.id),
                ) || []),
                ...(listOfCashOrCreditFiles?.filter(
                  (file) => !freshDocumentIdsForContinue.has(file?.id),
                ) || []),
              ];

              deletedDocuments = freshDocsForContinue?.data?.filter(
                (doc) =>
                  (approvalSubSections.includes(doc?.docType) &&
                    !bondAndTaxIds.has(doc?.id)) ||
                  (doc?.docType === DocTypes.CASH_CREDIT_SALE_DOCUMENTS &&
                    !cashOrCreditIds.has(doc?.id)),
              );
            } else {
              // NON CASH/CREDIT FLOW
              const approvalFileIds = new Set(
                internalApprovalActualDocs?.map((doc) => doc?.id),
              );

              newDocuments = internalApprovalActualDocs?.filter(
                (file) => !freshDocumentIdsForContinue?.has(file?.id),
              );

              deletedDocuments = freshDocsForContinue?.data?.filter(
                (doc) =>
                  (approvalSubSections.includes(doc?.docType) &&
                    !approvalFileIds.has(doc?.id)) ||
                  doc?.docType === DocTypes.CASH_CREDIT_SALE_DOCUMENTS,
              );
            }
            if (newDocuments?.length || deletedDocuments?.length) {
              isContinueSubmitRef.current = false;
              handleSaveContinue();
            } else {
              const projectReceivedStatus = freshDocsForContinue?.data?.some(
                (doc) => doc?.docType === DocTypes.PROJECT_INFORMATION_SHEET,
              )
                ? ProjectInformationSheetState.RECEIVED
                : ProjectInformationSheetState.NOT_RECEIVED;
              let approvalInfo = {
                jobId: stepOneJobId,
                name: formValues?.onSiteContactName,
                phone: formValues?.onSiteContactPhone,
                email: formValues?.onSiteContactEmail,
                dropDown1: formValues?.dropdown1,
                dropDown2: formValues?.dropdown2,
                dropDown3: formValues?.dropdown3,
                projectBondStatus: formValues?.projectBondStatus,
                taxExempt: taxExempt,
                jobSite: formValues?.jobSiteAddress,
                jobSiteLat:
                  formValues?.jobSiteAddressCoordinates?.split(",")[0],
                jobSiteLang:
                  formValues?.jobSiteAddressCoordinates?.split(",")[1],
                projectInformationSheet: projectReceivedStatus,
                poInfo1: formValues?.poInfo1,
                poInfo2: formValues?.poInfo2,
                poInfo3: formValues?.poInfo3,
                poInfo4: formValues?.poInfo4,
                cashOrCreditSale: formValues?.cashOrCreditSale,
              };
              const { data } = await refetchJobDetails();
              const isApprovalInfoChanged = hasApprovalInfoChanged(
                approvalInfo,
                data,
              );
              if (isApprovalInfoChanged) {
                isContinueSubmitRef.current = false;
                handleSaveContinue();

                return;
              }
              isContinueSubmitRef.current = false;
              await approveInternalCompleteRequest();
              await refetchJobDetails();
              setIsProjectApprovedModal(true);
            }
          };
          const handleSaveContinue = async () => {
            if (currentStep === 1) {
              const selectedCompanyCustomer = companiesData?.find(
                (item) => item?.id === formValues?.customerAccount,
              );
              const selectedContact = contactsData?.find(
                (item) => item?.id === formValues?.contactName,
              );

              const contactDetails = {
                companyId: selectedContact?.companyId,
                company: selectedContact?.company,
                lastName: selectedContact?.lastName,
                notes: selectedContact?.notes,
                isPrimaryContact: false,
                jobId: selectedContact?.jobId,
                email: selectedContact?.email,
                id: selectedContact?.id,
                phone: selectedContact?.phone,
                firstName: selectedContact?.firstName,
                isDriver: selectedContact?.isDriver,
              };
              if (isSubmittingRef.current || isLoading) return;
              isSubmittingRef.current = true;
              try {
                const errors = checkIsFilesUploaded();
                if (errors.length > 0) {
                  errors.map((err) => message.error(err));
                  setCurrentStep(1);
                } else {
                  // project stageId need not to be changed when job name only changes
                  const hasNameFieldChanged =
                    normalizeEmpty(formValues?.jobName).trim() !==
                    normalizeEmpty(jobDetails?.jobName).trim();
                  const hasChanges = hasChangesInJobLead(
                    {
                      jobName: formValues?.jobName,
                      companyId: formValues?.customerAccount,
                      contactDetails: selectedContact?.id,
                      dropOff: formValues?.deliveryAddress,
                      dropOffLatLng: formValues?.deliveryAddressCoordinates,
                      salesPerson: formValues?.salesPerson,
                      projectInformationSheet: projectInformation,
                      taxExemptStatus: taxExempt,
                      notes: formValues?.notes,
                    },
                    jobDetails,
                  );
                  let data;
                  if (
                    hasNameFieldChanged ||
                    hasChanges ||
                    jobDetails?.stageId === jobStatus.jobLead
                  ) {
                    data = await saveJobLead(
                      {
                        companyId: formValues?.customerAccount,
                        companyName: selectedCompanyCustomer?.companyName ?? "",
                        jobName: formValues?.jobName,
                        dropOff: formValues?.deliveryAddress,
                        dropOffLatLng: formValues?.deliveryAddressCoordinates,
                        projectInformationSheet: projectInformation,
                        taxExemptStatus: taxExempt,
                        salesPerson: formValues?.salesPerson,
                        contactDetails: contactDetails,
                        stageId:
                          hasChanges ||
                          jobDetails?.stageId === jobStatus.jobLead
                            ? jobStatus?.materialTruckDetails
                            : jobDetails?.stageId,
                        jobId:
                          editable || stepOneJobId ? stepOneJobId : "noJobId",
                        notes: formValues?.notes,
                        cloneFromJobId: selectedImportJobId,
                        resetOrders: resetOrders,
                      },
                      {
                        onSuccess: (data) => {
                          if (!editable && stepOneJobId === "") {
                            setStepOneJobId(String(data?.leadObj?.id));
                            setStepOneJobNumber(
                              Number(data?.leadObj?.jobNumber),
                            );
                            message.success(jobCreatedSuccessfully);
                          } else {
                            message.success(jobUpdatedSuccessfully);
                          }
                          handleDisableResetOrders();
                        },
                      },
                    );
                  }
                  const documentsData = await refecthDocuments();
                  const documentIds = new Set(
                    documentsData?.data?.map((doc) => doc?.id),
                  );
                  const jobFiles = [];
                  if (projectFile && !Array.isArray(projectFile)) {
                    const isDeprecated =
                      projectFile?.docType ===
                      DocTypes.DEPRECATED_PROJECT_INFORMATION_SHEET;
                    jobFiles.push({
                      ...projectFile,
                      id: isDeprecated ? undefined : projectFile?.id,
                    });
                  }
                  if (taxFile && !Array.isArray(taxFile)) {
                    const isDeprecated =
                      taxFile?.docType ===
                      DocTypes.DEPRECATED_TAX_EXEMPT_CERTIFICATE;
                    jobFiles.push({
                      ...taxFile,
                      id: isDeprecated ? undefined : taxFile?.id,
                    });
                  }
                  const jobFilesIds = new Set(
                    jobFiles.map((file) => file?.id).filter(Boolean),
                  );
                  const newDocuments = jobFiles?.filter(
                    (file) => !documentIds?.has(file?.id),
                  );
                  const deletedDocuments = documentsData?.data?.filter(
                    (doc) =>
                      (doc?.docType === DocTypes.PROJECT_INFORMATION_SHEET ||
                        doc?.docType === DocTypes.TAX_EXEMPT_CERTIFICATE) &&
                      !jobFilesIds?.has(doc?.id),
                  );
                  const deletedDocumentIds = deletedDocuments?.map(
                    (item) => item?.id,
                  );
                  if (deletedDocuments?.length > 0 && !!stepOneJobId) {
                    await deleteDocument({
                      recordIds: [...deletedDocumentIds],
                      leadId: stepOneJobId,
                    });
                  }
                  if (newDocuments.length > 0) {
                    const uploadedFiles = await Promise.all(
                      newDocuments.map(async (file) => {
                        if (file !== null && file !== undefined) {
                          const payload = {
                            ...file,
                            leadId:
                              editable || stepOneJobId !== ""
                                ? (data?.["data"]?.["id"] ?? stepOneJobId)
                                : (data?.["leadObj"]?.["id"] ?? stepOneJobId),
                            assignedTo:
                              editable || stepOneJobId !== ""
                                ? (data?.["data"]?.["jobNumber"] ??
                                  stepOneJobNumber)
                                : (data?.["leadObj"]?.["jobNumber"] ??
                                  stepOneJobNumber),
                            date: moment().format(
                              DateFormat.MM_DD_YYYY_SLASHED,
                            ),
                          };
                          const response = await addDocuments(payload);
                          return response?.message === ApiResponse.SUCCESS
                            ? { ...response.data, name: file.name }
                            : null;
                        }
                      }),
                    );
                    if (!hasChanges) {
                      await saveMaterailAndTruckDetails({
                        jobId: stepOneJobId,
                        stageId: jobStatus.materialTruckDetails,
                        jobType:
                          jobDetails?.jobType ??
                          TruckMaterialJobType.TRUCKING_JOB,
                      });
                    }
                  }
                  setCurrentStep(2);
                  refetchJobDetails();
                }
              } catch (error) {
                if (error.response?.status === 400)
                  message.error(error.response?.data?.message);
                isSubmittingRef.current = false;
              } finally {
                setValue("importJobId", selectedImportJobId);
                isSubmittingRef.current = false;
              }
            } else if (currentStep === 2) {
              if (
                truckDetails?.length === 0 &&
                materialTruckList?.length === 0
              ) {
                setCurrentStep(2);
                return message.error(addTruckDetails);
              } else if (
                truckDetails?.length > 0 ||
                materialTruckList?.length > 0
              ) {
                const { data } = await saveMaterailAndTruckDetails({
                  jobId: stepOneJobId,
                  truckAndMaterials: stepTwoPayload,
                });

                const freshTrucks =
                  getFormattedOrderList(
                    data?.material,
                    TruckMaterialType.TRUCK_TYPE,
                  ) ?? [];

                const freshMaterials =
                  getFormattedOrderList(
                    data?.material,
                    TruckMaterialType.MATERIAL_TYPE,
                  ) ?? [];

                resetTruckDetails();
                resetTruckMaterial();
                appendTruckDetail(freshTrucks);
                freshMaterials.forEach((item) => addTruckMaterial(item));
                setCurrentStep(currentStep + 1);
                message.success(jobUpdatedSuccessfully);
                refetchJobDetails();
              }
            } else if (currentStep === 3) {
              const { data } = await refetchJobDetails();
              if (
                data?.stageId === jobStatus.quoteShared ||
                (editable && buildQuotePastStates.includes(data?.stageId)) ||
                (!editable && buildQuotePastStates.includes(data?.stageId))
              ) {
                setCurrentStep(currentStep + 1);
                message.success(jobUpdatedSuccessfully);
                setQuoteSharedStatus(false);
                refetchJobDetails();
              } else {
                setCurrentStep(3);
                message.error(downloadQuoteToProceed);
              }
            } else if (currentStep === 4) {
              if (isSubmittingRef.current || isLoading) return;
              isSubmittingRef.current = true;
              const { data: customerDocData } =
                await refecthDocuments(stepOneJobId);
              const documentIds = new Set(
                customerDocData?.map((doc) => doc?.id),
              );
              const newDocuments = uploadedCustomerApprovedFiles?.filter(
                (file) => !documentIds?.has(file?.id),
              );
              const { data } = await refetchJobDetails();
              if (newDocuments?.length > 0) {
                const uploadedFiles = await Promise.all(
                  newDocuments.map(async (file) => {
                    if (file !== null) {
                      const payload = {
                        ...file,
                        leadId: stepOneJobId,
                        assignedTo: stepOneJobNumber,
                        date: moment().format(DateFormat.MM_DD_YYYY_SLASHED),
                        signatureVerified: true,
                        name: file.name,
                      };
                      const response = await addDocuments(payload);
                      return response?.message === ApiResponse.SUCCESS
                        ? { ...response.data, name: file.name }
                        : null;
                    }
                  }),
                );
                await saveMaterailAndTruckDetails({
                  jobId: stepOneJobId,
                  stageId: jobStatus.quoteApproved,
                  jobType: jobDetails?.jobType,
                });
                isSubmittingRef.current = false;
                message.success(jobUpdatedSuccessfully);
                setCurrentStep(currentStep + 1);
                refetchJobDetails();
                handleCustomerApprovedFiles([]);
              } else if (quoteApprovedStates.includes(data?.stageId)) {
                isSubmittingRef.current = false;
                message.success(jobUpdatedSuccessfully);
                setCurrentStep(currentStep + 1);
                refetchJobDetails();
                handleCustomerApprovedFiles([]);
                return;
              } else {
                isSubmittingRef.current = false;
                setCurrentStep(4);
                message.error(uploadCustomerApprovalFile);
              }
            } else if (currentStep === 5) {
              if (isSubmittingRef.current || isLoading) {
                return;
              }
              isSubmittingRef.current = true;
              const { data } = await refetchJobDetails();
              const isCashOrCredit = !!formValues?.cashOrCreditSale;
              const resolvedJobNumber =
                stepOneJobNumber || data?.jobNumber || "";
              // Only perform document validation checks if cashOrCreditSale is false
              if (!isCashOrCredit) {
                const isProjectInfoMissing = !hasDocsForSections(
                  projectInformationSheetSection,
                  listOfInternalApprovalFiles,
                );
                const areOtherSectionsMissing = !hasDocsForSections(
                  InternalApprovalSubSections.filter(
                    (section) =>
                      !projectInformationSheetSection.includes(section),
                  ),
                  listOfInternalApprovalFiles,
                );
                if (isProjectInfoMissing || areOtherSectionsMissing) {
                  setCurrentStep(5);
                  if (isProjectInfoMissing && areOtherSectionsMissing) {
                    message.error(atleastOneFileIsNeeded);
                  } else if (isProjectInfoMissing) {
                    message.error(projectSheetIsNeeded);
                  } else {
                    message.error(atleastOneFileIsNeeded);
                  }
                  isSubmittingRef.current = false;
                  return;
                }
              }
              const internalApprovalActualDocs =
                listOfInternalApprovalFiles?.map((doc) =>
                  doc?.docType === DocTypes.DEPRECATED_BOND_INFO_DOCUMENT
                    ? { ...doc, id: undefined }
                    : doc?.docType ===
                        DocTypes.DEPRECATED_TAX_EXEMPT_CERTIFICATE
                      ? { ...doc, id: undefined }
                      : doc,
                ) ?? [];
              //Tax and Bond Info check is separate
              const bondInfoDoc = internalApprovalActualDocs.find(
                (doc) => doc?.docType === DocTypes.BOND_INFO_DOCUMENT,
              );
              if (
                formValues?.projectBondStatus === BondStatusValue.bonded &&
                !bondInfoDoc
              ) {
                message.error(bondInfoDocumentNeeded);
                isSubmittingRef.current = false;
                return;
              }
              const taxDoc = internalApprovalActualDocs.find(
                (doc) => doc?.docType === DocTypes.TAX_EXEMPT_CERTIFICATE,
              );
              if (taxExempt === taxExemptStatusValue.exempt && !taxDoc) {
                isSubmittingRef.current = false;
                message.error(taxExemptDocumentNeeded);
                return;
              }

              // already persisted by a prior draft save are not treated as new.
              const freshDocsResult = await refecthDocuments();
              const freshDocumentIds = new Set(
                freshDocsResult?.data?.map((doc) => doc?.id),
              );
              let newDocuments, deletedDocuments;

              if (!isCashOrCredit) {
                //NON CASH OR CREDIT REGULAR FLOW
                const approvalFileIds = new Set(
                  internalApprovalActualDocs?.map((doc) => doc?.id),
                );

                newDocuments = internalApprovalActualDocs?.filter(
                  (file) => !freshDocumentIds?.has(file?.id),
                );

                deletedDocuments = freshDocsResult?.data?.filter(
                  (doc) =>
                    (approvalSubSections.includes(doc?.docType) &&
                      !approvalFileIds.has(doc?.id)) ||
                    doc?.docType === DocTypes.CASH_CREDIT_SALE_DOCUMENTS,
                );
              } else {
                // CASH OR CREDIT UPLOAD FLOW
                const bondAndTaxIds = new Set(
                  internalApprovalActualDocs
                    ?.filter(
                      (file) =>
                        file?.docType === DocTypes.BOND_INFO_DOCUMENT ||
                        file?.docType === DocTypes.TAX_EXEMPT_CERTIFICATE,
                    )
                    ?.map((file) => file?.id),
                );
                const cashOrCreditIds = new Set(
                  listOfCashOrCreditFiles?.map((doc) => doc?.id),
                );

                newDocuments = [
                  ...(internalApprovalActualDocs?.filter(
                    (file) =>
                      (file?.docType === DocTypes.BOND_INFO_DOCUMENT ||
                        file?.docType === DocTypes.TAX_EXEMPT_CERTIFICATE ||
                        file?.docType ===
                          DocTypes.DEPRECATED_BOND_INFO_DOCUMENT ||
                        file?.docType ===
                          DocTypes.DEPRECATED_TAX_EXEMPT_CERTIFICATE) &&
                      !freshDocumentIds.has(file?.id),
                  ) || []),
                  ...(listOfCashOrCreditFiles?.filter(
                    (file) => !freshDocumentIds.has(file?.id),
                  ) || []),
                ];
                deletedDocuments = freshDocsResult?.data?.filter(
                  (doc) =>
                    (approvalSubSections.includes(doc?.docType) &&
                      !bondAndTaxIds.has(doc?.id)) ||
                    (doc?.docType === DocTypes.CASH_CREDIT_SALE_DOCUMENTS &&
                      !cashOrCreditIds.has(doc?.id)),
                );
              }

              const deletedDocumentIds = deletedDocuments?.map(
                (doc) => doc?.id,
              );
              deletedDocumentIds?.length &&
                (await handleDeleteDocument(deletedDocumentIds));

              // Only validate rejected sections if cashOrCreditSale is false
              if (
                !isCashOrCredit &&
                internalApprovalPastStates.includes(data?.stageId) &&
                !validateRejectedSectionsHaveDocs(
                  jobDetails?.approvalDocuments,
                  newDocuments,
                )
              ) {
                isSubmittingRef.current = false;
                setCurrentStep(5);
                return;
              }
              const approvedSections = isCashOrCredit
                ? cashOrCreditAllowedSections
                : approvalSubSections;
              const isNewDocumentsAdded =
                newDocuments?.some((doc) =>
                  approvedSections.includes(doc?.docType),
                ) || deletedDocumentIds?.length > 0;

              await Promise.all(
                newDocuments?.map(async (file) => {
                  if (file !== null) {
                    const payload = {
                      ...file,
                      leadId: stepOneJobId,
                      assignedTo: resolvedJobNumber,
                      date: moment().format(DateFormat.MM_DD_YYYY_SLASHED),
                      name: file.name,
                    };
                    const response = await addDocuments(payload);
                    return response?.message === ApiResponse.SUCCESS
                      ? { ...response.data, name: file.name }
                      : null;
                  }
                }),
              );
              const documentsDataAfterUpload = await refecthDocuments();
              const cashOrCreditDocuments =
                documentsDataAfterUpload?.data
                  ?.filter(
                    (doc) =>
                      doc?.docType === DocTypes.CASH_CREDIT_SALE_DOCUMENTS,
                  )
                  ?.map((doc) => doc.id)
                  ?.filter(Boolean) ?? [];
              const projectReceivedStatus =
                documentsDataAfterUpload?.data?.some(
                  (doc) => doc?.docType === DocTypes.PROJECT_INFORMATION_SHEET,
                )
                  ? ProjectInformationSheetState.RECEIVED
                  : ProjectInformationSheetState.NOT_RECEIVED;
              const stageId = isNewDocumentsAdded
                ? jobStatus.l1ApprovalPending
                : getStageId(jobDetails?.stageId, 5);
              let approvalInfo = {
                jobId: stepOneJobId,
                name: formValues?.onSiteContactName,
                phone: formValues?.onSiteContactPhone,
                email: formValues?.onSiteContactEmail,
                dropDown1: formValues?.dropdown1,
                dropDown2: formValues?.dropdown2,
                dropDown3: formValues?.dropdown3,
                projectBondStatus: formValues?.projectBondStatus,
                taxExempt: taxExempt,
                jobSite: formValues?.jobSiteAddress,
                jobSiteLat:
                  formValues?.jobSiteAddressCoordinates?.split(",")[0],
                jobSiteLang:
                  formValues?.jobSiteAddressCoordinates?.split(",")[1],
                stageId: stageId,
                projectInformationSheet: projectReceivedStatus,
                poInfo1: formValues?.poInfo1,
                poInfo2: formValues?.poInfo2,
                poInfo3: formValues?.poInfo3,
                poInfo4: formValues?.poInfo4,
                cashOrCreditSale: formValues?.cashOrCreditSale,
              };
              const isApprovalInfoChanged = hasApprovalInfoChanged(
                approvalInfo,
                data,
              );
              let response;
              if (
                isApprovalInfoChanged ||
                isNewDocumentsAdded ||
                data?.stageId === jobStatus?.quoteApproved ||
                data?.stageId === jobStatus?.internalApprovalDraft
              ) {
                const approvalDocuments = getFormattedApprovalDocuments(
                  documentsDataAfterUpload?.data,
                  jobDetails?.approvalDocuments,
                  newDocuments,
                  deletedDocuments,
                  isApprovalInfoChanged ||
                    data?.stageId === jobStatus?.quoteApproved ||
                    data?.stageId === jobStatus?.internalApprovalDraft ||
                    isNewDocumentsAdded,
                );
                const payload =
                  isApprovalInfoChanged ||
                  data?.stageId === jobStatus?.internalApprovalDraft
                    ? { ...approvalInfo, stageId: jobStatus.l1ApprovalPending }
                    : approvalInfo;
                response = await saveInternalApproval({
                  ...payload,
                  ...(formValues?.cashOrCreditSale
                    ? {
                        cashOrCreditProjectDocs: cashOrCreditDocuments,
                        approvalDocuments: undefined,
                      }
                    : {
                        approvalDocuments: approvalDocuments,
                        cashOrCreditProjectDocs: undefined,
                      }),
                });
              }
              if (!response) {
                handleSubmitNavigate();
              } else {
                if (response?.message === ApiResponse.SUCCESS) {
                  isSubmittingRef.current = false;
                  if (
                    response?.data?.stageId ===
                    jobStatus.internalApprovalComplete
                  ) {
                    message.success(ordersReadyForDispatch);
                    const latestDocs = await refecthDocuments();
                    handleCashOrCreditFiles(
                      latestDocs?.data?.filter(
                        (doc) =>
                          doc?.docType === DocTypes.CASH_CREDIT_SALE_DOCUMENTS,
                      ) ?? [],
                    );
                    handleInternalApprovalFiles(
                      latestDocs?.data?.filter((doc) =>
                        approvalSubSections.includes(doc?.docType),
                      ) ?? [],
                    );
                    await refetchJobDetails();
                  } else {
                    handleSubmitNavigate();
                  }
                } else {
                  isSubmittingRef.current = false;
                  message.error(failedToSubmitQuote);
                  setCurrentStep(5);
                }
              }
            } else {
              currentStep < 5
                ? setCurrentStep(currentStep + 1)
                : setCurrentStep(5);
            }
          };

          return (
            <>
              <Modal
                visible={isProjectApprovedModalOpen}
                maskClosable={false}
                onCancel={() => {
                  setIsProjectApprovedModal(false);
                  navigate.push(
                    `/app/trucking-material${window.location?.hash}`,
                  );
                }}
                footer={false}
                className={styles.modalWrapper}
              >
                <div className={styles.DispatchModal}>
                  <img src={greenTickIcon} alt="APPROVED" width={"10%"} />
                  <ConditionalRender
                    visible={
                      jobDetails?.stageId === jobStatus.internalApprovalComplete
                    }
                  >
                    <h2>Project Approved</h2>
                    <p>Go to the Request Dispatch page to place an order</p>
                    <Button
                      type={ButtonType.PRIMARY}
                      size={ButtonSize.LARGE}
                      className={styles.ButtonSave}
                      onClick={approveDispatchRequest}
                    >
                      {DispatchModalConstants.ApproveJobForDispatchLabel}
                    </Button>
                  </ConditionalRender>
                  <ConditionalRender
                    visible={jobDetails?.stageId === jobStatus.dispatchApproved}
                  >
                    <h2>{DispatchModalConstants.DispatchApprovedLabel}</h2>
                    <ConditionalRender visible={hasCustomerAccess}>
                      <Button
                        type={ButtonType.DEFAULT}
                        size={ButtonSize.LARGE}
                        className={styles.ButtonSave}
                        onClick={() => {
                          setIsProjectApprovedModal(false);
                          navigate.push(
                            generatePath(appRoutes.CUSTOMER_PROFILE_ID, {
                              id: jobDetails?.companyId,
                            }),
                            {
                              customerName: jobDetails?.companyName,
                              navigatedFromQuote: true,
                            },
                          );
                        }}
                      >
                        {DispatchModalConstants.NaviagateToCustomerProfile}
                      </Button>
                    </ConditionalRender>
                  </ConditionalRender>
                </div>
              </Modal>

              <div className={styles.CreateJobButtonContainer}>
                <div
                  className={styles.ButtonsContainer}
                  onClick={handleOpenWarningModal}
                >
                  <img
                    src={BackButton}
                    alt="back-button"
                    className={styles.backButtonStyle}
                  />
                  <h1 className={styles.NewJobHeading}>
                    {editable ? "Edit Job" : "New Job"}
                  </h1>
                </div>
                <div className={styles.ButtonsContainer}>
                  <ConditionalRender
                    visible={
                      (currentStep === 1 &&
                        (presentStageId === jobStatus.jobLead ||
                          presentStageId === undefined)) ||
                      (currentStep === 2 &&
                        (presentStageId === jobStatus.materialTruckDetails ||
                          presentStageId === jobStatus.dispatchApproved)) ||
                      (currentStep === 5 &&
                        (presentStageId === jobStatus.internalApprovalDraft ||
                          presentStageId === jobStatus.quoteApproved) &&
                        !jobIsDispatchApproved)
                    }
                  >
                    <Button
                      type={ButtonType.DEFAULT}
                      size={ButtonSize.LARGE}
                      className={styles.ButtonSave}
                      onClick={handleSaveDraft}
                      disabled={isSubmittingRef.current}
                    >
                      {SAVE_DRAFT}
                    </Button>
                  </ConditionalRender>
                  <ConditionalRender visible={currentStep > 1}>
                    <Button
                      type={ButtonType.DEFAULT}
                      size={ButtonSize.LARGE}
                      className={styles.ButtonSave}
                      onClick={handlePrevStep}
                    >
                      {PREVIOUS}
                    </Button>
                  </ConditionalRender>
                  <ConditionalRender
                    visible={currentStep === 5}
                    fallback={
                      <Button
                        type={ButtonType.PRIMARY}
                        size={ButtonSize.LARGE}
                        className={styles.ButtonSave}
                        onClick={
                          // handleNextStep
                          handleSubmit(handleSaveContinue)
                        }
                        disabled={isLoading}
                        loading={isSubmittingRef.current}
                      >
                        {SAVE_AND_CONTINUE}
                      </Button>
                    }
                  >
                    {" "}
                    <Button
                      type={ButtonType.PRIMARY}
                      size={ButtonSize.LARGE}
                      className={styles.ButtonSave}
                      onClick={
                        // handleNextStep
                        handleSubmit(handleSaveContinue)
                      }
                      disabled={isSubmittingRef.current}
                    >
                      {SAVE}
                    </Button>
                    <Button
                      type={ButtonType.PRIMARY}
                      size={ButtonSize.LARGE}
                      className={styles.ButtonSave}
                      disabled={
                        isContinueDisabled || isContinueSubmitRef.current
                      }
                      loading={isContinueSubmitRef.current}
                      onClick={handleSubmit(handleContinue)}
                    >
                      {CONTINUE}
                    </Button>
                  </ConditionalRender>
                </div>
              </div>
              <div className={styles.FormContainer}>
                <TNStepperForm
                  type={StepperTypeAttributes.STEPPER_STEPS}
                  currentStep={currentStep}
                  handleCurrentStep={handleCurrentStep}
                  currentFormStep={currentFormStep}
                  steps={steps}
                  showPricingDetailsView={false}
                />
              </div>
            </>
          );
        }}
      </Form>
    </div>
  );
};

export default CreateJobForm;
